/* ═══════════════════════════════════════════════════════════════
   REVIEW — the side list of changes (docs/SYNC_PLAN.md §2.3).

   One row per change: kind, a short excerpt, its decision. Click jumps.
   Under it: the receipt line ("N already in this project") and the
   switch that reveals what the copy already had, read-only (principle 3).
   A "Waiting upstream at <variant>" line when an update is held further
   up the chain. Nothing waiting → the empty state.

   Two headings — Content now, Formatting when forma rows arrive (the
   Formatting heading is hidden while it has nothing to show).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Change, Decision } from '../../shared/sync';
import EmptyState from '../components/EmptyState';
import { StatusBadge } from '../components/StatusBadge';
import { Switch } from '../components/ui/switch';
import { CollapsibleSection } from '../components/ui/collapsible-section';
import { Button } from '../components/ui/button';
import type { SyncFeedRow, UpstreamPending } from './types';
import './review.css';

type KindLabel = 'Added' | 'Removed' | 'Changed' | 'Moved' | 'Formatting' | 'Already here';

/** What the list calls a change. `invert` = self-review (draft vs published). */
export function kindLabel(c: Change, invert = false): KindLabel {
  if (c.kind === 'reconciled') return 'Already here';
  if (c.kind === 'moved') return 'Moved';
  if (c.kind === 'changed') return c.whole ? 'Formatting' : 'Changed';
  if (c.kind === 'added') return invert ? 'Removed' : 'Added';
  return invert ? 'Added' : 'Removed';
}

export function excerptOf(c: Change, invert = false): string {
  const clip = (s: string) => (s || '').replace(/\s+/g, ' ').trim();
  if (c.kind === 'changed' && !c.whole) {
    const from = clip(invert ? c.after : c.before);
    const to = clip(invert ? c.before : c.after);
    return `${from || '…'} → ${to || '…'}`;
  }
  return clip(c.after || c.before) || (c.whole ? 'Paragraph' : '…');
}

function kindClass(label: KindLabel): string {
  switch (label) {
    case 'Added':
      return 'sf-review-kind sf-review-kind-added';
    case 'Removed':
      return 'sf-review-kind sf-review-kind-removed';
    case 'Moved':
      return 'sf-review-kind sf-review-kind-moved';
    case 'Changed':
    case 'Formatting':
      return 'sf-review-kind sf-review-kind-changed';
    default:
      return 'sf-review-kind';
  }
}

function DecisionBadge({ d }: { d: Decision | undefined }) {
  if (d === 'accept') return <StatusBadge status="accepted" label="Accepted" variant="success" />;
  if (d === 'reject') return <StatusBadge status="rejected" label="Rejected" variant="danger" />;
  return <StatusBadge status="waiting" label="Waiting" variant="neutral" />;
}

export interface ReviewPanelProps {
  /** Heading over the list. */
  title: string;
  items: Change[];
  decisions?: Record<string, Decision>;
  currentId: string | null;
  onJump: (id: string) => void;
  /** Self-review: labels read draft-vs-published; no decision badges. */
  invert?: boolean;
  showDecisions?: boolean;

  /** Pending rows for this target — a picker shows when there is more than one. */
  rows?: SyncFeedRow[];
  activeRowId?: string | null;
  onPickRow?: (rowId: string) => void;
  /** Forma rows (Formatting heading) — hidden while empty. */
  formaRows?: SyncFeedRow[];

  reconciled?: Change[];
  reconciledCount?: number;
  upstreamPending?: UpstreamPending[];

  loading?: boolean;
  loadError?: string | null;
  onRetry?: () => void;
  /** Shown when there is nothing at all to review. */
  emptyTitle?: string;
  /** Names a change in words instead of the text excerpt (a forma's
   *  settings: "Footer, left: order changed"). */
  describe?: (c: Change) => string;
  /** A muted second line under a change (e.g. a forma setting that applies
   *  to the whole document). */
  describeNote?: (c: Change) => string | null;
  /** The receipt line's noun ("already in this project" by default). */
  receiptLabel?: string;
}

export function ReviewPanel({
  title,
  items,
  decisions = {},
  currentId,
  onJump,
  invert = false,
  showDecisions = true,
  rows = [],
  activeRowId = null,
  onPickRow,
  formaRows = [],
  reconciled = [],
  reconciledCount = 0,
  upstreamPending = [],
  loading = false,
  loadError = null,
  onRetry,
  emptyTitle = 'Everything is up to date with your masters.',
  describe,
  describeNote,
  receiptLabel = 'already in this project',
}: ReviewPanelProps) {
  const textOf = (c: Change) => (describe ? describe(c) : excerptOf(c, invert));
  const [showAll, setShowAll] = React.useState(false);
  const receipt = Math.max(reconciledCount, reconciled.length);
  const nothing = !loading && !loadError && !items.length && !upstreamPending.length;

  return (
    <aside className="sf-review-panel" aria-label={title}>
      <section className="sf-review-panel-section">
        <h2 className="sf-review-panel-heading">{title}</h2>

        {loading && (
          <p className="sf-review-panel-note" role="status">
            Loading master updates…
          </p>
        )}

        {loadError && (
          <div role="alert">
            <p className="sf-review-panel-note">{loadError}</p>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                Try again
              </Button>
            )}
          </div>
        )}

        {rows.length > 1 && onPickRow && (
          <ul className="sf-review-list" aria-label="Updates waiting">
            {rows.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className="sf-review-item"
                  aria-current={r.id === activeRowId ? 'true' : undefined}
                  onClick={() => onPickRow(r.id)}
                >
                  <span className="sf-review-kind">
                    {r.toVersion != null ? `v${r.toVersion}` : 'Update'}
                  </span>
                  <span className="sf-review-excerpt">From {r.upstreamLabel || 'its master'}</span>
                  <span />
                </button>
              </li>
            ))}
          </ul>
        )}

        {upstreamPending.map((u) => (
          <p key={`${u.layerId}:${u.targetId}`} className="sf-review-panel-note" role="status">
            Waiting upstream at {u.label} — it is reviewed there first. Nothing to do here yet.
          </p>
        ))}

        {nothing && <EmptyState compact title={emptyTitle} />}

        {items.length > 0 && (
          <ul className="sf-review-list" aria-label="Changes">
            {items.map((c) => {
              const label = kindLabel(c, invert);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    className="sf-review-item"
                    aria-current={c.id === currentId ? 'true' : undefined}
                    onClick={() => onJump(c.id)}
                  >
                    <span className={kindClass(label)}>{label}</span>
                    <span className="sf-review-excerpt-wrap">
                      <span className="sf-review-excerpt" title={textOf(c)}>
                        {textOf(c)}
                      </span>
                      {describeNote?.(c) && (
                        <span className="sf-review-excerpt-note">{describeNote(c)}</span>
                      )}
                    </span>
                    {showDecisions ? <DecisionBadge d={decisions[c.id]} /> : <span />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {receipt > 0 && (
        <section className="sf-review-panel-section">
          <CollapsibleSection
            id="sf-review-receipt"
            title={`${receipt} ${receiptLabel}`}
            expanded={showAll}
            onToggle={() => setShowAll((v) => !v)}
          >
            <ul className="sf-review-list" aria-label={receiptLabel}>
              {reconciled.map((c) => (
                <li key={c.id} className="sf-review-item is-readonly">
                  <span className="sf-review-kind">Already here</span>
                  <span className="sf-review-excerpt" title={textOf(c)}>
                    {textOf(c)}
                  </span>
                  <span />
                </li>
              ))}
            </ul>
          </CollapsibleSection>
          <label className="sf-review-switch">
            <span>Show everything the master changed</span>
            <Switch
              checked={showAll}
              onCheckedChange={setShowAll}
              aria-label="Show everything the master changed"
            />
          </label>
        </section>
      )}

      {formaRows.length > 0 && (
        <section className="sf-review-panel-section">
          <h2 className="sf-review-panel-heading">Formatting</h2>
          <ul className="sf-review-list">
            {formaRows.map((r) => (
              <li key={r.id} className="sf-review-item is-readonly">
                <span className="sf-review-kind">
                  {r.toVersion != null ? `v${r.toVersion}` : 'Update'}
                </span>
                <span className="sf-review-excerpt">From {r.upstreamLabel || 'its master'}</span>
                <span />
              </li>
            ))}
          </ul>
        </section>
      )}
    </aside>
  );
}
